import type {
    IDateFilterParams,
    INumberFilterParams,
    ISetFilterParams,
    ITextFilterParams,
} from '@ag-grid-community/core';

/**
 * Different types of filter that are available on an ag-grid column
 * @see {@link https://www.ag-grid.com/javascript-data-grid/filtering/}
 * @see {@link https://www.ag-grid.com/javascript-data-grid/filter-text/}
 * @see {@link https://www.ag-grid.com/javascript-data-grid/filter-number/}
 * @see {@link https://www.ag-grid.com/javascript-data-grid/filter-date/}
 */
export enum FilterType {
    Text = 'agTextColumnFilter',
    Number = 'agNumberColumnFilter',
    Float = 'agFloatColumnFilter',
    Date = 'agDateColumnFilter',
    Set = 'agSetColumnFilter',
}

const defaultFilterParams:
    | ITextFilterParams
    | INumberFilterParams
    | IDateFilterParams = {
    // The number of conditions that can be applied to the filter (AND/OR)
    // only set to 1 as the API currently only supports one condition
    maxNumConditions: 1,
    buttons: ['clear'],
};

/**
 * A default set of filter parameters that can be used to configure
 * the filter UI for a column
 * @see {@link https://www.ag-grid.com/javascript-data-grid/filter-text/#text-filter-parameters}
 */
export const defaultIdFilterParams: ITextFilterParams = {
    ...defaultFilterParams,
    filterOptions: ['equals'],
};

export const defaultTextFilterParams: ITextFilterParams = {
    ...defaultFilterParams,
    // The types of different text filters available to choose from
    filterOptions: ['contains'],
};

export const defaultNumberFilterParams: INumberFilterParams = {
    ...defaultFilterParams,
    // The types of different number filters available to choose from
    filterOptions: [
        'equals',
        'greaterThan',
        'greaterThanOrEqual',
        'lessThan',
        'lessThanOrEqual',
    ],
};

export const cvssDecimalFilterParams: INumberFilterParams = {
    ...defaultNumberFilterParams,
    allowedCharPattern: '\\d\\.',
};

export const defaultDateFilterParams: IDateFilterParams = {
    ...defaultFilterParams,
    // The types of different date filters available to choose from
    filterOptions: [
        'equals',
        'lessThan',
        'lessThanOrEqual',
        'greaterThan',
        'greaterThanOrEqual',
    ],
    // Cell values are ISO date strings (e.g. '2025-01-15'). Parse the date
    // part only to avoid UTC vs local-time mismatches when comparing.
    comparator: (
        filterDate: Date,
        cellValue: string | null | undefined,
    ): number => {
        if (!cellValue) return -1;
        const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(cellValue));
        if (!match) return -1;
        const cell = new Date(
            Number(match[1]),
            Number(match[2]) - 1,
            Number(match[3]),
        );
        if (cell < filterDate) return -1;
        if (cell > filterDate) return 1;
        return 0;
    },
};

export const defaultSetFilterParams: ISetFilterParams = {
    // SetFilterParams are not compatible with defaultFilterParams type because of differences in the api, setting defaults manually
    buttons: ['clear'],
    defaultToNothingSelected: true,
};

/**
 * A default set of filter parameters that can be used to configure a text filter with boolean values
 * This ensures that an ag-grid to graphql conversion works properly.
 * @see {@link setTextValue} in /utils/ag-datasource/graphql-conversion.ts
 */
export const defaultBooleanFilterParams: ITextFilterParams = {
    filterOptions: ['equals'],
};
